import { daysBetween } from './calendar'
import type { SeriesPoint } from './chartScale'
import type { Projection } from './projection'

/**
 * Turn stored observations into chart series (D-087).
 *
 * Pure and record-shaped rather than storage-shaped: it takes plain
 * `{ date, value }` pairs so it can be fixture-tested without a database, and
 * so the same function serves weight, waist and anything added later.
 *
 * **x is days from the origin, not an index.** Using the array index would space
 * a weigh-in taken after a three-week break exactly one step from the one
 * before it, drawing a gap as though it were a day. The same reasoning that put
 * `dayIndex` in the export (D-060) applies to the picture.
 */

import type { CalendarDate } from '~/types/dates'

export interface DatedValue {
  readonly date: CalendarDate
  readonly value: number
}

export function toSeries(observations: readonly DatedValue[], origin: CalendarDate): SeriesPoint[] {
  return observations.map((observation) => ({
    x: daysBetween(origin, observation.date),
    y: observation.value,
  }))
}

/** The earliest date across several series, so they share one x origin. */
export function earliestDate(
  ...groups: readonly (readonly DatedValue[])[]
): CalendarDate | undefined {
  const dates = groups.flat().map((observation) => observation.date)
  return dates.length === 0 ? undefined : dates.reduce((a, b) => (a <= b ? a : b))
}

/**
 * The projected path, as two points: where the trend is now, and the target.
 *
 * **Deliberately a straight dashed segment rather than a week-by-week curve.**
 * The projection is compounding arithmetic (D-068), so a curve would be
 * *drawable* — but it would imply the app knows which week each intermediate
 * weight falls in, which is exactly the false precision D-068 avoided by
 * reporting a week span and an approximate month rather than a date. Two points
 * say "from here to there, in about this long" and nothing more.
 *
 * Returns an empty array whenever there is nothing honest to draw.
 */
export function projectionSeries(projection: Projection, anchorX: number): SeriesPoint[] {
  if (projection.kind !== 'projected') return []

  /* The path the engine itself calls recommended; falling back to the first
     plan path keeps the chart drawable if that key is ever renamed. */
  const path =
    projection.plan.find((candidate) => candidate.key === 'recommended') ?? projection.plan[0]
  if (path === undefined || path.outcome.kind !== 'reaches') return []

  return [
    { x: anchorX, y: projection.anchorKg as number },
    { x: anchorX + path.outcome.weeks * 7, y: projection.target.weightKg as number },
  ]
}
